/**
 * GKV invoice explain screen
 *
 * Route: `/webAppOnboarding/insurance-invoice-explain`. Timeline for invoice billing path
 * before checkout.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('invoice')` — wrong/missing billing type → insurance-select
 *   or matching explain route; `useSaveOnboardingStep('insurance_invoice_process_overview')`.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { space } from '../../../styles/onboardingUiTokens';
import { ListTimeline, ListTimelineItem } from '../components/content/ListTimeline';
import { LoadingSpinner } from '../components/elements/LoadingSpinner';
import { Notice } from '../components/elements/Notice';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { H1 } from '../components/typography/HeadingLevels';
import { useInsuranceExplainBillingGuard } from '../hooks/useInsuranceExplainBillingGuard';
import { useSelectedInsurance } from '../hooks/useSelectedInsurance';
import { SHARED_INVOICE_TIMELINE } from '../insurance/copy';
import { displayInsuranceShortName } from '../insurance/displayInsuranceName';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`;

const DynamicLoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

export function WebOnboardingInsuranceInvoiceProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_invoice_process_overview');
  const navigate = useNavigate();
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const { insurance, loading: insuranceLoading, error: insuranceError } = useSelectedInsurance();

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );

  const insuranceName = ctx.insuranceName ?? '';

  const insuranceShortName = useMemo(() => {
    if (!insurance) {
      return '';
    }
    return displayInsuranceShortName(insurance);
  }, [insurance]);

  const bonusStep = useMemo(() => {
    if (!insurance?.bonus?.hasBonus) {
      return null;
    }
    const bonus = insurance.bonus;
    return {
      title: `Prämie von der ${insuranceShortName}`,
      body: bonus.description?.trim() ?? '',
    };
  }, [insurance, insuranceShortName]);

  useInsuranceExplainBillingGuard('invoice');

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'invoice',
      insuranceId: insurance?.id,
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
    if (ctx.forwardPath) {
      navigate(ctx.forwardPath);
    }
  };

  const bodyContent = (() => {
    if (insuranceLoading) {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (insuranceError) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          {insuranceError}
        </Notice>
      );
    }

    if (ctx.billingType !== 'invoice' || (!insuranceName && !insurance)) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          Bitte wähle zuerst deine Krankenkasse aus.
        </Notice>
      );
    }

    const timelineInsuranceName = insuranceShortName || insuranceName;
    const step2Body = SHARED_INVOICE_TIMELINE.step2BodyTemplate.replace(
      '{insuranceName}',
      timelineInsuranceName,
    );

    return (
      <ContentColumn>
        <H1>Dein Kursablauf mit der {insuranceName || timelineInsuranceName}</H1>
        <ListTimeline>
          <ListTimelineItem
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                💪
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.primary}
            title={SHARED_INVOICE_TIMELINE.step1Title}
          >
            {SHARED_INVOICE_TIMELINE.step1Body}
          </ListTimelineItem>
          <ListTimelineItem
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                📨
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.primary}
            title={SHARED_INVOICE_TIMELINE.step2Title}
          >
            {step2Body}
          </ListTimelineItem>
          {bonusStep ? (
            <ListTimelineItem
              marker={
                <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                  🎁
                </span>
              }
              highlighted
              highlightColor={colors.success}
              title={bonusStep.title}
            >
              {bonusStep.body}
            </ListTimelineItem>
          ) : null}
        </ListTimeline>
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={6}
      onBack={handleBack}
      forwardLabel="Weiter"
      forwardDisabled={insuranceLoading}
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingBody>
        <div style={{ paddingBottom: space.xl }}>{bodyContent}</div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
